import { toaster } from '@/components/ui/toaster';
import {
  billingSettingsQueryKey,
  fetchBillingSettings,
  readBillingSettings,
} from '@/hooks/queries/use-billing-settings-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  OrganizationBillingSettingsData,
  UpdateOrganizationBillingContactRequest,
} from '@fluenceur/types';
import { ErrorCode } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTargetChangedError } from './use-billing-action-scope';
import { useBillingSettingsScope } from './use-billing-settings-scope';

const requestSchema = z
  .object({
    expectedBillingEmail: z.string().email().max(254).nullable(),
    billingEmail: z.string().trim().email().max(254).toLowerCase(),
  })
  .strict();

class BillingContactChangedError extends Error {}

export function useUpdateBillingContact(snapshot?: OrganizationBillingSettingsData) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingSettingsScope();
  const key = billingSettingsQueryKey(scope);
  return useMutation<
    OrganizationBillingSettingsData,
    AxiosError<ApiResponse> | Error,
    UpdateOrganizationBillingContactRequest,
    typeof scope
  >({
    mutationKey: [...key, 'contact'],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (request) => {
      assertCurrent(scope);
      const valid = requestSchema.safeParse(request);
      const known = readBillingSettings(snapshot);
      if (
        !valid.success ||
        !known?.canManage ||
        known.organizationId !== scope.organizationId ||
        known.brandId !== scope.brandId ||
        valid.data.expectedBillingEmail !== known.billingEmail
      )
        throw new Error('No authorized billing contact update');
      const fresh = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
        staleTime: 0,
        retry: false,
      });
      assertCurrent(scope);
      if (!fresh.canManage) throw new Error('Billing contact permission changed');
      if (fresh.billingEmail !== valid.data.expectedBillingEmail)
        throw new BillingContactChangedError('Billing contact changed');
      const response = await api.put<ApiResponse<unknown>>(
        '/dashboard/billing/settings/contact',
        valid.data
      );
      assertCurrent(scope);
      const saved = readBillingSettings(response.data.data);
      if (
        !response.data.success ||
        !saved ||
        saved.organizationId !== scope.organizationId ||
        saved.brandId !== scope.brandId ||
        saved.billingEmail !== valid.data.billingEmail
      )
        throw new Error('Invalid billing contact update');
      const confirmed = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
        staleTime: 0,
        retry: false,
      });
      assertCurrent(scope);
      if (!confirmed.canManage || confirmed.billingEmail !== saved.billingEmail)
        throw new BillingTargetChangedError('Billing contact permission changed');
      return confirmed;
    },
    onSuccess: (data, _request, target) => {
      if (!target) return;
      assertCurrent(target);
      client.setQueryData(billingSettingsQueryKey(target), data);
      toaster.success({
        title: t('common.success'),
        description: t('billing.settings.contactSaved'),
      });
    },
    onError: async (error, _request, target) => {
      if (error instanceof BillingTargetChangedError || !target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      const response = (error as AxiosError<ApiResponse>).response;
      const conflict =
        error instanceof BillingContactChangedError ||
        (response?.status === 409 &&
          response.data?.code === ErrorCode.E_STRIPE_BILLING_IDENTITY_CONFLICT);
      toaster.error({
        title: t('common.error'),
        description: t(
          conflict ? 'billing.settings.contactConflict' : 'billing.settings.contactError'
        ),
      });
      if (conflict) await client.invalidateQueries({ queryKey: key, exact: true });
    },
  });
}
